<template>
  <IndicatorCard
    :loading="loading"
    :has-data="rows.length > 0"
    :is-result="isResult"
    :no-data-message="$t('Nenhum dado de deslocamento encontrado')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

    <v-row class="ma-0" justify="center">
      <v-col cols="12" class="d-flex justify-start flex-wrap" style="gap: 24px;">
        <strong>{{ $t('Tempo Total') }}: {{ tempoTotal }}</strong>
        <strong>{{ $t('Tempo Médio') }}: {{ tempoMedio }}</strong>
      </v-col>

      <v-col cols="12">
        <v-data-table
          :loading="loading"
          :loading-text="$t('Carregando...')"
          :no-data-text="$t('Nenhum registro encontrado')"
          :headers="headers"
          :items="rows"
          class="main-table mt-3"
          dense
          :hide-default-footer="!filterProp.data"
          :items-per-page="7"
          :mobile-breakpoint="0"
        >
          <template v-slot:[`item.serviceOrderNumber`]="{ item }">
            #{{ item.serviceOrderNumber }}
          </template>
          <template v-slot:[`item.startDate`]="{ item }">
            {{ formatDateTime(item.startDate) }}
          </template>
          <template v-slot:[`item.endDate`]="{ item }">
            {{ formatDateTime(item.endDate) }}
          </template>
        </v-data-table>
        <div class="pagination" v-if="!filterProp.data">
          <div class="item-pagination block-two">
            <v-pagination
              class="pagination-item"
              v-model="page"
              :length="totalPages"
              :total-visible="9"
            ></v-pagination>
          </div>
        </div>
      </v-col>
    </v-row>

    <template #review>
      <v-row class="ma-0">
        <v-col cols="12" v-if="isResult && review">
          <strong>{{ $t('Análise crítica') }}</strong>
          <p v-html="review"></p>
        </v-col>
        <v-col cols="12" v-else-if="!isResult">
          <tiptap-vuetify
            v-model="filter.review"
            :extensions="extensions"
            :label="$t('Análise crítica')"
            :placeholder="
              $t('Digite sua análise crítica para o indicador abaixo')
            "
            :card-props="{ flat: true, outlined: true, rounded: true }"
            class="primary-input"
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
      </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import { getIndicatorById } from '../../../../../indicators/indicatorRegistry'
import ApiService from '../../../../../../../../services/ApiService'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import moment from 'moment'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

export default {
  components: {
    IndicatorCard,
    DateRangePicker,
    TiptapVuetify,
  },

  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    filterProp: Object,
    institution: Object,
    unidades: Array,
    review: String,
    isResult: { type: Boolean, default: false },
  },

  async created() {
    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades

    const lastMonthStart = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')
    const lastMonthEnd = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    this.filter.startDate = lastMonthStart
    this.filter.endDate = lastMonthEnd
    this.startDate = lastMonthStart
    this.endDate = lastMonthEnd

    if (this.filterProp) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    this.list()
  },

  data: () => ({
    excelLoading: false,
    extensions: [
      History, Blockquote, Link, Underline, Strike, Italic, ListItem,
      BulletList, OrderedList,
      [Heading, { options: { levels: [1, 2, 3] } }],
      Bold, Code, HorizontalRule, Paragraph, HardBreak,
    ],
    loading: true,
    rows: [],
    tempoTotal: '00H00m',
    tempoMedio: '00H00m',
    page: 1,
    totalPages: 1,
    headers: [
      { text: 'Nº O.S.', value: 'serviceOrderNumber', align: 'center', sortable: false },
      { text: 'Início do Deslocamento', value: 'startDate', align: 'center', sortable: false },
      { text: 'Fim do Deslocamento', value: 'endDate', align: 'center', sortable: false },
      { text: 'Tempo de Deslocamento', value: 'time', align: 'center', sortable: false },
      { text: 'Responsável', value: 'responsibleName', align: 'center', sortable: false },
      { text: 'Descrição da Etapa', value: 'description', align: 'left', sortable: false },
    ],
    filter: {
      page: 1,
      pageSize: 7,
      institutionId: null,
      unidadeIds: [],
      startDate: null,
      endDate: null,
      review: '',
    },
    startDate: null,
    endDate: null,
    apiService: new ApiService(),
  }),

  computed: {
    indicator() {
      return getIndicatorById('displacement_time') || {}
    },
  },

  methods: {
    formatDateTime(value) {
      return value ? moment(value).format('DD/MM/YYYY HH:mm') : '-'
    },

    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${this.indicator.legacyKey}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, { ...this.filter, pageSize: 999999 })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.filter.page = 1
      this.page = 1
      if (this.filterProp) this.filterProp.data = null
      this.list()
    },

    setInfos() {
      const model = {
        chart: getIndicatorById('displacement_time').legacyKey,
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    extractData(response) {
      const content = response?.content || {}
      this.rows = content.paged?.objects || []
      this.tempoTotal = content.tempoTotal || '00H00m'
      this.tempoMedio = content.tempoMedio || '00H00m'
      this.totalPages = content.paged?.pager?.totalPages || 1
    },

    async list() {
      this.loading = true
      try {
        if (this.filterProp.data) {
          this.extractData(JSON.parse(this.filterProp.data))
          return
        }

        if (!this.isResult) this.setDates()
        this.filter.institutionId = this.institution.id
        this.filter.pageSize = 7
        this.filter.unidadeIds = this.unidades

        const response = await this.apiService.post(
          this.apiEndpointOverride || 'reports/displacementTime',
          this.filter
        )
        this.extractData(response)
      } catch (e) {
        this.rows = []
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    page() {
      this.filter.page = this.page
      this.list()
    },

    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>
